/**
 * Five-track readiness ribbon. Plan §11 + Kaiyang Lesson §25.7 — every
 * track is rendered SEPARATELY, never summed. The reminder copy is
 * visible to the user (not just in the JSON disclaimer).
 *
 * Visual rules from docs/UI-SYSTEM.md §1.4:
 *   - Legal: 4px solid left rail
 *   - Evidence: 2px double top border
 *   - Market force: 1.5px dashed border + "(non-legal)" suffix
 *   - Ecosystem: 1.5px dotted border + "(non-legal)" suffix
 *   - Continuing: calendar notch label + mono ratio
 */

import type { ReadinessSummary } from "../services/api";

interface Props {
  readiness: ReadinessSummary | null;
  loading: boolean;
}

const NON_LEGAL_BADGE = "non-legal";

export function ReadinessHeader({ readiness, loading }: Props) {
  return (
    <section
      aria-label="Five-track readiness"
      style={{
        padding: "var(--space-16) var(--space-24)",
        background: "var(--color-surface-sunken)",
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: "var(--space-12)",
        }}
      >
        <h2 className="type-h2">Readiness · five independent tracks</h2>
        <p className="type-caption" style={{ margin: 0 }}>
          {readiness?.disclaimer ?? "Tracks shown side-by-side; never aggregated."}
        </p>
      </header>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: "var(--space-12)",
        }}
      >
        <Track
          label="Legal"
          ratio={readiness ? `${readiness.tracks.legal.with_evidence} / ${readiness.tracks.legal.applicable}` : "—"}
          caption="hard_law with evidence guidance"
          style={{
            borderLeft: "4px solid var(--color-text-primary)",
            background: "var(--color-surface-elevated)",
          }}
          loading={loading}
        />
        <Track
          label="Evidence standard"
          ratio={readiness ? String(readiness.tracks.evidence_standard.applicable) : "—"}
          caption="applicable evidence_standard rules"
          style={{
            borderTop: "3px double var(--color-text-primary)",
            background: "var(--color-surface-elevated)",
          }}
          loading={loading}
        />
        <Track
          label="Market force"
          badge={NON_LEGAL_BADGE}
          ratio={readiness ? String(readiness.tracks.market_force.applicable) : "—"}
          caption="market_force overlays"
          style={{
            border: "1.5px dashed var(--color-border-strong)",
            background: "var(--color-surface)",
          }}
          loading={loading}
        />
        <Track
          label="Ecosystem"
          badge={NON_LEGAL_BADGE}
          ratio={readiness ? String(readiness.tracks.ecosystem.applicable) : "—"}
          caption="ecosystem_signal overlays"
          style={{
            border: "1.5px dotted var(--color-border-strong)",
            background: "var(--color-surface)",
          }}
          loading={loading}
        />
        <Track
          label="Continuing"
          ratio={
            readiness
              ? `${readiness.tracks.continuing_obligation.with_obligation} / ${readiness.tracks.continuing_obligation.total_applicable}`
              : "—"
          }
          caption="applicable rules with continuingObligation"
          style={{
            borderLeft: "4px solid var(--status-candidate-pending)",
            background: "var(--color-surface-elevated)",
          }}
          loading={loading}
        />
      </div>
    </section>
  );
}

interface TrackProps {
  label: string;
  ratio: string;
  caption: string;
  style: React.CSSProperties;
  badge?: string;
  loading: boolean;
}

function Track({ label, ratio, caption, style, badge, loading }: TrackProps) {
  return (
    <article
      style={{
        padding: "var(--space-12) var(--space-16)",
        borderRadius: "var(--radius-8)",
        minHeight: 80,
        opacity: loading ? 0.6 : 1,
        transition: `opacity var(--duration-normal) var(--easing-decelerate)`,
        ...style,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--space-8)",
          marginBottom: "var(--space-8)",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: 13 }}>{label}</span>
        {badge && (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--color-text-secondary)",
              background: "var(--status-unknown-config-soft)",
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-full)",
              padding: "1px var(--space-8)",
            }}
          >
            {badge}
          </span>
        )}
      </header>
      <div
        className="type-mono"
        style={{
          fontSize: 22,
          fontWeight: 600,
          color: "var(--color-text-primary)",
          marginBottom: "var(--space-4)",
        }}
        aria-live="polite"
      >
        {loading ? "…" : ratio}
      </div>
      <p className="type-caption" style={{ margin: 0 }}>
        {caption}
      </p>
    </article>
  );
}
